import { useState, useEffect, useCallback } from 'react';
import { messaging, getToken, deleteToken } from '../firebase';
import { pushTokenAPI } from '../api/client';
import { track } from '../utils/analytics';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

export function useNotification(userId) {
  const [permission, setPermission] = useState(window.Notification?.permission || 'default');
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    const supported = 'Notification' in window && 'serviceWorker' in navigator && messaging !== null && !!VAPID_KEY;
    setIsSupported(supported);
    if (supported) {
      setPermission(window.Notification.permission);
    }
  }, []);

  // 마운트 시 이미 권한이 granted면 토큰이 DB에 등록되어 있는지 확인 → isRegistered 복원
  useEffect(() => {
    if (!messaging || !userId || window.Notification?.permission !== 'granted') return;

    (async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        });
        if (token) {
          // DB에 실제로 등록되어 있는지 확인
          const exists = await pushTokenAPI.exists(userId, token);
          setIsRegistered(exists);
        }
      } catch {
        // 토큰 발급 실패 — 등록 안 된 상태로 유지
      }
    })();
  }, [userId]);

  // 포그라운드 메시지 수신은 App.jsx의 글로벌 리스너에서 처리

  const requestPermission = useCallback(async () => {
    if (!isSupported || !userId) return false;
    setIsLoading(true);

    try {
      const result = await window.Notification.requestPermission();
      setPermission(result);
      track('notification-permission', { granted: result === 'granted' });

      if (result !== 'granted') {
        setIsLoading(false);
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        await pushTokenAPI.register(userId, token);
        setIsRegistered(true);
        setIsLoading(false);
        return true;
      }

      setIsLoading(false);
      return false;
    } catch (err) {
      console.error('[useNotification] Error:', err);
      setIsRegistered(false);
      setIsLoading(false);
      return false;
    }
  }, [isSupported, userId]);

  const disableNotification = useCallback(async () => {
    if (!userId) return false;
    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
      if (token) {
        await pushTokenAPI.unregister(userId, token);
        // FCM 토큰 자체를 삭제해야 재등록 시 새 토큰이 발급됨
        await deleteToken(messaging);
      }
      setIsRegistered(false);
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('[useNotification] Disable error:', err);
      setIsLoading(false);
      return false;
    }
  }, [userId]);

  return {
    permission,
    isSupported,
    isLoading,
    isEnabled: permission === 'granted' && isRegistered,
    requestPermission,
    disableNotification,
  };
}
